import { Stack, TextField, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useChangePasswordMutation } from 'api/auth/auth.api'
import { hasLowerCase, hasSpecialChar, hasUpperCase } from 'helpers/global.helper/global.helper'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ContainedIconButton from 'ui/components/ContainedIconButton/ContainedIconButton'
import RequirementField from 'ui/components/RequirementField/RequirementField'

import styles from './ChangePasswordBox.module.less'

export default function ChangePasswordBox(): ReactElement {
  const [newPassword1, setNewPassword1] = useState(``)
  const [newPassword2, setNewPassword2] = useState(``)
  const [currentPassword, setCurrentPassword] = useState(``)
  const [meetLowerCaseReq, setMeetLowerCaseReq] = useState(false)
  const [meetUppercaseOrSpecialCharReq, setMeetUppercaseOrSpecialCharReq] = useState(false)
  const [meetPasswordLengthReq, setMeetPasswordLengthReq] = useState(false)
  const [hasCurrentPassword, setHasCurrentPassword] = useState(false)
  const { mutateAsync, isSuccess } = useChangePasswordMutation()
  const { t } = useTranslation()

  const buttonDisabled =
    !hasCurrentPassword === false &&
    !meetUppercaseOrSpecialCharReq === false &&
    !meetLowerCaseReq === false &&
    !meetPasswordLengthReq === false &&
    newPassword1 === newPassword2
      ? false
      : true

  function validatePasswordRequirements(pass: string): void {
    setMeetLowerCaseReq(hasLowerCase(pass))
    setMeetUppercaseOrSpecialCharReq(hasUpperCase(pass) || hasSpecialChar(pass))
    setMeetPasswordLengthReq(pass.length >= 8)
  }

  function validateCurrentPassword(pass: string): void {
    if (pass !== ``) setHasCurrentPassword(true)
    else setHasCurrentPassword(false)
  }

  const currPswd = useRef<HTMLInputElement | null>(null)
  const pswd1 = useRef<HTMLInputElement | null>(null)
  const pswd2 = useRef<HTMLInputElement | null>(null)

  function resetPswdFields(): void {
    if (isSuccess && currPswd.current && pswd1.current && pswd2.current) {
      currPswd.current.value = ``
      pswd1.current.value = ``
      pswd2.current.value = ``
      setMeetUppercaseOrSpecialCharReq(false)
      setMeetPasswordLengthReq(false)
      setMeetLowerCaseReq(false)
    }
  }

  useEffect(() => {
    if (isSuccess) resetPswdFields()
  })

  return (
    <Grid className={styles.ChangePasswordBox_PasswordBox}>
      <Grid>
        <Typography className={styles.ChangePasswordBox_ChangePasswordTitle}>{t(`Change password`)}</Typography>
      </Grid>
      <Grid size={{ xs: 10 }}>
        <TextField
          inputRef={currPswd}
          className={styles.ChangePasswordBox_TextField}
          variant="outlined"
          label={t(`Current password`)}
          type="password"
          autoComplete="current-password"
          aria-label={t(`Current password`)}
          onChange={(event) => {
            setCurrentPassword(event.target.value)
            validateCurrentPassword(event.target.value)
          }}
          slotProps={{
            htmlInput: { id: `currentpass`, 'data-testid': `currentpass` },
          }}
        />
      </Grid>
      <Grid size={{ xs: 10 }}>
        <TextField
          inputRef={pswd1}
          className={styles.ChangePasswordBox_TextField}
          variant="outlined"
          label={t(`New password`)}
          aria-label={t(`New password`)}
          type="password"
          onChange={(event) => {
            setNewPassword1(event.target.value)
            validatePasswordRequirements(event.target.value)
          }}
          slotProps={{
            htmlInput: { id: `newpass1`, 'data-testid': `newpass1` },
          }}
        />
      </Grid>
      <Grid size={{ xs: 10 }}>
        <TextField
          inputRef={pswd2}
          className={styles.ChangePasswordBox_TextField}
          variant="outlined"
          label={t(`Repeat password`)}
          aria-label={t(`Repeat password`)}
          type="password"
          onChange={(event) => {
            setNewPassword2(event.target.value)
          }}
          slotProps={{
            htmlInput: { id: `newpass2`, 'data-testid': `newpass2` },
          }}
        />
      </Grid>
      <Stack direction="column" alignItems="flex-start" className={styles.ChangePasswordBox_ReqField} spacing={1}>
        <RequirementField
          meetLowerCaseReq={meetLowerCaseReq}
          meetUppercaseOrSpecialCharReq={meetUppercaseOrSpecialCharReq}
          meetPasswordLengthReq={meetPasswordLengthReq}
          matchingPasswords={newPassword1 === newPassword2 && newPassword1 !== `` && newPassword2 !== ``}
        />
      </Stack>
      <Grid container direction="row" justifyContent="flex-end">
        <Grid className={styles.ChangePasswordBox_Button}>
          <ContainedIconButton
            onClick={() => {
              mutateAsync({ newPassword1, newPassword2, currentPassword })
              setNewPassword1(``)
              setNewPassword2(``)
            }}
            disabled={buttonDisabled}
            label={t(`Update password`)}
            icon="fal fa-key"
            aria-label={t(`Update password`)}
            color="primary"
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
